import React, { useState, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { captureAndUploadPdf, analyzeReportLayout } from './reports/captureAndUpload';
import ReportPreflightModal from './reports/ReportPreflightModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Constants, narrative builder
import { TOGGLEABLE_SECTIONS, FINDINGS_TITLES, fmtDate } from './reports/constants';
import { buildCaseHistoryNarrative } from './reports/narrative';

// Layout pieces
import { ReportHeader } from './reports/layout/ReportHeader';
import { PatientStrip } from './reports/layout/PatientStrip';
import { SignatureFooter } from './reports/layout/SignatureFooter';

// Registry + sections only used directly by the deferred-page layout
import { renderSectionById } from './reports/sectionRegistry';
import { ResultsGridSection } from './reports/sections/ResultsGridSection';
import { RecommendationsAdviceSection } from './reports/sections/RecommendationsAdviceSection';
import { TympanometryFullPage } from './reports/TympanometrySections';

// Builder sidebar (left aside)
import { BuilderSidebar } from './reports/BuilderSidebar';
import LandscapePrompt from './LandscapePrompt';

const ReportsPanel = ({
  patient: propPatient,
  rightEarData,
  leftEarData,
  preTestData,
  impedanceData,
  speechData,
  specialTestsData,
  oaeData,
  soundfieldData,
  abrData,
  pediatricData,
  tinnitusData,
  sessionId,
  audiologistName,
  audiologistUserId,
  clinicalImpression,
  recommendations,
  audiogramMode = 'combined',
  onPersist, // (partial) => save to backend
  // Read-only past-report viewer props: when set, the panel hydrates its
  // internal state from a saved snapshot, hides BuilderSidebar, and skips
  // debounced auto-save so the archived report is truly immutable.
  initialBuilder = null,
  hideBuilder = false,
  previewId = 'report-preview',
  // Controlled-prop pair — when the parent (e.g.
  // HearingReportPreviewModal) drives the "Fit to 1 A4" toggle from
  // its own toolbar, it passes `compactLayout` + `onCompactLayoutChange`
  // and the panel's local state becomes a no-op. Undefined ⇒ purely
  // internal state (default for the live editor with BuilderSidebar).
  compactLayout: compactLayoutProp,
  onCompactLayoutChange,
}) => {
  // ========== NAV-006 F-012 — re-derive patient from session ==========
  // The `patient` prop can be stale in live editing mode (e.g., audiologist
  // opened the queue with patient v1, front-desk edited the patient in
  // another tab, audiologist then clicks the Reports tab). The session's
  // `patient_id` on the backend is the source of truth. We re-derive
  // ONLY in live editing mode:
  //   • `hideBuilder=false` → live editing → re-derive to catch mid-session
  //     patient edits/merges.
  //   • `hideBuilder=true` → snapshot / live-preview viewer → trust the
  //     prop verbatim so historical snapshots stay frozen and the preview
  //     modal's already-re-derived patient isn't re-fetched redundantly.
  // If the fetch fails for any reason, we fall back to the prop so the
  // panel always renders something.
  //
  // NOTE: The destructured prop is aliased to `propPatient` so the
  // useEffect closures below reference the RAW prop, not the derived
  // `patient` const declared later. A previous revision reassigned the
  // `patient` parameter after the effects were defined, which meant
  // `setLivePatient(patient)` closed over the reassigned value and never
  // synced the newly-loaded patient (name/age/gender + MRD would render
  // from the initial `{ patient_id }` fallback forever).
  const [livePatient, setLivePatient] = useState(propPatient);
  useEffect(() => {
    // Keep `livePatient` in sync with the prop when it changes (e.g.,
    // parent swapped active test / opened a different snapshot, or the
    // preview modal finished loading the real patient after the session).
    setLivePatient(propPatient);
  }, [propPatient]);
  useEffect(() => {
    if (hideBuilder || !sessionId) return undefined;
    let alive = true;
    (async () => {
      try {
        const sr = await axios.get(`${API}/sessions/${sessionId}`);
        const pid = sr.data?.patient_id;
        if (!pid || !alive) return;
        const pr = await axios.get(`${API}/patients/${pid}`);
        if (alive && pr?.data) setLivePatient(pr.data);
      } catch {
        // Re-derive is best-effort. Prop patient (already set in state)
        // remains the fallback.
      }
    })();
    return () => { alive = false; };
  }, [sessionId, hideBuilder]);
  // Everywhere below in this component, use `patient` bound to the
  // re-derived value so the JSX doesn't need touching. `const` (not a
  // parameter reassignment) so effects that reference the raw prop see
  // the true prop identity.
  const patient = livePatient || propPatient;
  // ========== Section config ==========
  // Sections can be overridden by a saved snapshot / live-preview modal
  // via `initialBuilder.sections`. Each entry only needs {id, enabled}
  // (order + label come from TOGGLEABLE_SECTIONS). Missing ids default
  // to their `defaultEnabled` value — additive rather than destructive.
  const [sections, setSections] = useState(() => {
    const overrides = initialBuilder?.sections;
    if (Array.isArray(overrides) && overrides.length) {
      const byId = new Map(overrides.map((o) => [o.id, o]));
      return TOGGLEABLE_SECTIONS.map((s) => ({
        id: s.id,
        label: s.label,
        enabled: byId.has(s.id)
          ? !!byId.get(s.id).enabled
          : s.defaultEnabled,
      }));
    }
    return TOGGLEABLE_SECTIONS.map((s) => ({
      id: s.id, label: s.label, enabled: s.defaultEnabled,
    }));
  });

  // ========== Editable fields ==========
  const [resultsText, setResultsText] = useState(
    initialBuilder?.clinical_impression ?? clinicalImpression ?? ''
  );
  const [recText, setRecText] = useState(
    initialBuilder?.recommendations
      ? (initialBuilder.recommendations || []).join('\n')
      : (recommendations || []).join('\n')
  );
  const [furtherAdvice, setFurtherAdvice] = useState(initialBuilder?.further_advice ?? '');
  const [license, setLicense] = useState(initialBuilder?.license ?? '');
  // Per-section findings narrative — keyed by section id (see FINDINGS_TITLES).
  // Each enabled section that has an entry in FINDINGS_TITLES contributes a
  // findings cell to the Results grid; the audiologist edits the text in the
  // matching textarea in BuilderSidebar.
  const [findings, setFindings] = useState(initialBuilder?.findings_by_section ?? {});
  const setFinding = (id, val) =>
    setFindings((prev) => ({ ...prev, [id]: val }));
  const [provisionalDiagnosis, setProvisionalDiagnosis] = useState(
    initialBuilder?.provisional_diagnosis ?? ''
  );
  // "Referred by" — hydrate from either:
  //   1. Persisted builder state (audiologist has already saved a value)
  //   2. Patient's linked referring doctor (from registration)
  //   3. Patient's free-text `referring_physician` fallback
  // Using `||` (truthy) not `??` (nullish) so we also fall through
  // empty strings — audiologists frequently save with a blank
  // "Referred by" and rely on the field auto-populating on re-open
  // once the front desk backfills the patient's referring doctor.
  const initialReferredBy = (
    initialBuilder?.referred_by
    || patient?.referring_doctor_name
    || patient?.referring_physician
    || ''
  );
  const [referredBy, setReferredBy] = useState(initialReferredBy);
  // MRD prefers the human-readable Medical Record Number (patient.mrd);
  // patient_id is the internal fallback when a patient has no MRD assigned.
  // The `useEffect` below re-syncs the field if the real patient loads
  // after the initial render (e.g., preview modal fetches session first,
  // then patient) — but only when the audiologist hasn't manually edited
  // the value (empty or still holding the patient_id fallback).
  const [mrdEdit, setMrdEdit] = useState(patient?.mrd || patient?.patient_id || '');
  useEffect(() => {
    if (!patient?.mrd) return;
    if (mrdEdit === '' || mrdEdit === patient?.patient_id) {
      setMrdEdit(patient.mrd);
    }
  }, [patient?.mrd, patient?.patient_id]);

  // ========== Layout preferences ==========
  const [tympPlacement, setTympPlacement] = useState('auto'); // auto | inline | separate
  const [audiogramSize, setAudiogramSize] = useState('large'); // standard | large | xlarge — only honored when Tymp is on new page
  const [showABC, setShowABC] = useState(false);
  const [showBing, setShowBing] = useState(false);
  const tuningForkFull = showABC || showBing;
  // Phase D / #8 — Compact 1-page layout. When ON:
  //   • Tymp is forced inline (never on p2)
  //   • Audiogram forced to 'standard' (small)
  //   • CSS class `.report-compact` tightens padding + font-sizes
  // Off by default so existing reports keep their generous spacing.
  // ─────────────────────────────────────────────────────────────
  // Controlled-prop pattern: if the parent passes `compactLayout`
  // + `onCompactLayoutChange` (e.g. HearingReportPreviewModal
  // exposes a "Fit to 1 A4" toggle in its own toolbar), the
  // ReportsPanel state is driven by the parent. Otherwise the
  // BuilderSidebar's own toggle drives the local state.
  const [compactLayoutInternal, setCompactLayoutInternal] = useState(false);
  const compactLayout = compactLayoutProp !== undefined
    ? compactLayoutProp
    : compactLayoutInternal;
  const setCompactLayout = onCompactLayoutChange || setCompactLayoutInternal;

  // ========== Clinic branding — sourced from Settings (2026-07-30) ==========
  // Previously this was maintained in localStorage inside a dedicated
  // "Clinic Branding" panel on the report builder — duplicating what
  // /settings/clinic already collects. User asked to remove the panel
  // and pull ONLY from the Settings source of truth so every report
  // stays in sync with the clinic profile automatically.
  //
  // Fallback: if the Settings load fails or the fields are blank, we
  // render a placeholder header on the report ("[Your Clinic Name —
  // set in Settings]") via ReportHeader so audiologists notice.
  const [clinic, setClinic] = useState({
    name: '', tagline: '', address_line1: '', address_line2: '',
    tel: '', email: '', logo_base64: null, logo_shape: 'circle',
    report_font: '',
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/clinic${sessionId ? `?session_id=${sessionId}` : ''}`);
        const s = r.data || {};
        if (cancelled) return;
        // Compose the two-line address the report layout expects.
        const line1 = s.address || '';
        const line2 = [s.city, s.state, s.pincode].filter(Boolean).join(', ');
        setClinic((prev) => ({
          ...prev,
          name: s.name || '',
          // Tagline is captured in Settings → Clinic Details. When present,
          // ReportHeader prints it as the small line above the clinic name.
          tagline: s.tagline || '',
          address_line1: line1,
          address_line2: line2,
          tel: s.phone || '',
          email: s.email || '',
          // Report typography chosen in Settings → Clinic Details → Typography.
          // Applied inline on the A4 preview root so both on-screen preview
          // and html2canvas PDF capture use the same family.
          report_font: s.report_font || '',
        }));
        // Best-effort logo fetch — API is /settings/clinic/logo which
        // returns 404 when the clinic has no logo uploaded. We ignore
        // errors here; ReportHeader gracefully handles a null logo.
        try {
          const lr = await axios.get(`${API}/settings/clinic/logo`, { responseType: 'blob' });
          if (cancelled) return;
          const reader = new FileReader();
          reader.onload = () => {
            if (!cancelled) setClinic((prev) => ({ ...prev, logo_base64: reader.result }));
          };
          reader.readAsDataURL(lr.data);
        } catch { /* no logo uploaded yet */ }
      } catch { /* keep placeholder header */ }
    })();
    return () => { cancelled = true; };
  }, []);
  // No-op setter for downstream compat — BuilderSidebar no longer edits
  // clinic branding but still receives the prop.
  const setClinic_readOnly = () => {};

  // Auto rule: if Reflex Decay, ET Dysfunction, or ETF-Intact are enabled, default to separate page
  const autoSeparatePage = !!(
    impedanceData?.reflex_decay?.enabled ||
    impedanceData?.et_dysfunction?.enabled ||
    impedanceData?.etf_intact?.enabled
  );
  // Compact 1-page mode wins over any Tymp placement / audiogram-size
  // choices — the whole point is to squeeze onto ONE sheet.
  const useSeparatePage = compactLayout
    ? false
    : (tympPlacement === 'separate' || (tympPlacement === 'auto' && autoSeparatePage));
  const effectiveAudiogramSize = compactLayout ? 'standard' : audiogramSize;

  // ========== Debounced auto-save ==========
  const saveTimer = useRef(null);
  // ── Bug 2 · Findings not persisting (2026-02-17) ─────────────────
  // Every debounced schedule stashes the CURRENT payload into
  // `pendingPayloadRef`. On unmount (e.g. tab-switch away from the
  // Reports tab), if a debounce is still pending, we synchronously
  // flush that payload so nothing the audiologist typed in the last
  // 800ms is lost.
  const pendingPayloadRef = useRef(null);
  const onPersistRef = useRef(onPersist);
  useEffect(() => { onPersistRef.current = onPersist; }, [onPersist]);
  useEffect(() => {
    // When viewing a saved snapshot, the panel is read-only — no writes.
    if (!onPersist || hideBuilder) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    const payload = {
      clinical_impression: resultsText,
      // Legacy fields retained for backward-compat with the existing
      // `test_sessions` document & PDF templates that read them by name.
      puretone_findings:    findings.pure_tone || '',
      immitence_findings:   findings.tympanometry || '',
      speech_findings:      findings.speech || '',
      // New per-section findings — the full map is also persisted so we
      // never lose narrative for sections that aren't covered by the
      // 3 named legacy columns above.
      findings_by_section:  findings,
      provisional_diagnosis: provisionalDiagnosis,
      referred_by: referredBy,
      further_advice: furtherAdvice,
      recommendations: recText.split('\n').map((l) => l.trim()).filter(Boolean),
      // Persist the audiologist's section-checkbox state so both the
      // Live Preview modal AND saved snapshots re-render only the
      // sections they explicitly enabled — no auto-derive from data.
      sections: sections.map((s) => ({ id: s.id, enabled: !!s.enabled })),
      license,
    };
    pendingPayloadRef.current = payload;
    saveTimer.current = setTimeout(() => {
      onPersist(payload);
      pendingPayloadRef.current = null;
    }, 800);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [
    resultsText, recText, findings, provisionalDiagnosis,
    referredBy, furtherAdvice, onPersist, sections, license, hideBuilder,
  ]);

  // Flush any pending save on unmount so tab-switches never silently
  // discard the last few keystrokes. Runs ONCE at teardown.
  useEffect(() => {
    return () => {
      if (pendingPayloadRef.current && onPersistRef.current) {
        try { onPersistRef.current(pendingPayloadRef.current); }
        catch { /* fire-and-forget — parent handles axios failure logging */ }
        pendingPayloadRef.current = null;
      }
    };
  }, []);

  const toggleSection = (id) =>
    setSections((s) => s.map((x) => (x.id === id ? { ...x, enabled: !x.enabled } : x)));
  const moveSection = (idx, dir) =>
    setSections((s) => {
      const next = [...s];
      const tgt = idx + dir;
      if (tgt < 0 || tgt >= next.length) return s;
      [next[idx], next[tgt]] = [next[tgt], next[idx]];
      return next;
    });

  const caseHistoryNarrative = useMemo(
    () => buildCaseHistoryNarrative(patient, preTestData?.case_history || {}),
    [patient, preTestData]
  );

  // Which test sections are currently toggled ON — used to decide which Results
  // findings cells to show. Impedance also shows when Tymp is on a separate page.
  const isEnabled = (id) => !!sections.find((s) => s.id === id && s.enabled);
  const buildResultEntries = () => {
    // Honour the section order in the sidebar so the audiologist's drag/drop
    // re-order is reflected in the Results grid too.
    return sections
      .filter((s) => s.enabled && FINDINGS_TITLES[s.id])
      .map((s) => ({
        key: s.id,
        title: FINDINGS_TITLES[s.id],
        text: findings[s.id] || '',
      }));
  };

  // Shared context consumed by the section registry's render functions.
  const sectionContext = {
    isEnabled,
    caseHistoryNarrative,
    rightEarData, leftEarData, preTestData, impedanceData, speechData,
    specialTestsData, oaeData, soundfieldData, abrData, pediatricData, tinnitusData,
    audiogramMode, audiogramSize: effectiveAudiogramSize, useSeparatePage, tuningForkFull,
    showABC, showBing,
    recText, furtherAdvice,
    provisionalDiagnosis,
    buildResultEntries,
  };

  const handlePrint = async () => {
    // 1. Capture the live preview DOM → render to a multi-page A4 PDF.
    // 2. Upload that PDF blob to the backend so the Reports archive stores
    //    the *exact* file the audiologist just printed (not a server-side
    //    placeholder template). Runs in the background — never blocks print.
    const el = document.getElementById(previewId);
    if (el && sessionId && !hideBuilder) {
      captureAndUploadPdf(el, sessionId).catch((e) => {
        console.warn('Report PDF upload failed — falling back to template PDF for this session:', e?.message);
      });
    } else if (sessionId && !hideBuilder) {
      // Fallback: the DOM isn't mounted (unlikely) — just flip status.
      axios.post(`${API}/sessions/${sessionId}/mark-printed`).catch(() => { });
    }
    window.print();
  };

  // Preflight: when the audiologist clicks Print, first open the "Looks
  // good?" modal so they can sanity-check page count + layout warnings
  // BEFORE the PDF is uploaded to GridFS and sent to the patient.
  const [preflightOpen, setPreflightOpen] = useState(false);
  const openPreflight = () => setPreflightOpen(true);
  const closePreflight = () => setPreflightOpen(false);
  const confirmPrint = () => {
    setPreflightOpen(false);
    // Defer a microtask so the modal teardown fully unmounts before
    // html2canvas runs — the modal overlay itself is outside
    // `#report-preview` so it doesn't affect capture, but this keeps the
    // print flow feeling instant.
    setTimeout(() => { handlePrint(); }, 0);
  };

  // Auto-fix dispatcher: each preflight warning can carry a `fixKey` that
  // maps to a one-click remedy. Applying a fix closes the modal so the
  // audiologist can see the updated preview (the watchdog dot will update
  // within ~400ms); they can re-open Print when satisfied.
  const applyPreflightFix = (key) => {
    if (key === 'tymp-inline') {
      setTympPlacement('inline');
    } else if (key === 'shrink-audiograms') {
      setAudiogramSize('standard');
    }
    setPreflightOpen(false);
  };

  // ---------- Silent layout watchdog ----------
  // Re-runs analyzeReportLayout whenever the report preview DOM changes
  // (section toggled, finding typed, audiogram edited, …). Exposes a
  // severity "dot" on the Print button so the audiologist can see at a
  // glance whether anything needs attention — before they ever click
  // Print. The analyze call is canvas-free (~5 ms), so debouncing at
  // 400ms is enough to avoid thrash without feeling laggy.
  const [layoutStatus, setLayoutStatus] = useState({ pageCount: 0, warnLevel: 'ok' });
  useEffect(() => {
    const el = document.getElementById(previewId);
    if (!el) return undefined;
    let timer = null;
    const severityOf = (warnings) =>
      warnings.some((w) => w.level === 'error') ? 'error'
        : warnings.some((w) => w.level === 'warn') ? 'warn'
          : warnings.length > 0 ? 'info'
            : 'ok';
    const run = () => {
      try {
        const a = analyzeReportLayout(el);
        setLayoutStatus({ pageCount: a.pageCount, warnLevel: severityOf(a.warnings) });
      } catch {
        // Never surface a broken analyzer to the user — this is a hint, not a gate.
      }
    };
    const debounced = () => { if (timer) clearTimeout(timer); timer = setTimeout(run, 400); };
    run();
    const observer = new MutationObserver(debounced);
    observer.observe(el, { childList: true, subtree: true, attributes: true, characterData: true });
    return () => { observer.disconnect(); if (timer) clearTimeout(timer); };
  }, []);

  // When the Tymp page is "New page", the conclusion block (Results + Recommendations/Advice
  // + Signature) is deferred to the end of the report so the ENT reads test data first.
  const mainPageSections = sections
    .filter((s) => s.enabled)
    .filter((s) => !(useSeparatePage && (s.id === 'results' || s.id === 'recommendations')));

  return (
    <div className="flex-1 flex flex-col md:flex-row min-h-0 bg-gray-100 overflow-hidden">
      {!hideBuilder && (
        <BuilderSidebar
          sections={sections}
          onToggleSection={toggleSection}
          onMoveSection={moveSection}
          clinic={clinic}
          setClinic={setClinic_readOnly}
          showABC={showABC}
          setShowABC={setShowABC}
          showBing={showBing}
          setShowBing={setShowBing}
          tympPlacement={tympPlacement}
          setTympPlacement={setTympPlacement}
          useSeparatePage={useSeparatePage}
          autoSeparatePage={autoSeparatePage}
          audiogramSize={audiogramSize}
          setAudiogramSize={setAudiogramSize}
          compactLayout={compactLayout}
          setCompactLayout={setCompactLayout}
          ptFindings={findings.pure_tone || ''} setPtFindings={(v) => setFinding('pure_tone', v)}
          immFindings={findings.tympanometry || ''} setImmFindings={(v) => setFinding('tympanometry', v)}
          speechFindings={findings.speech || ''} setSpeechFindings={(v) => setFinding('speech', v)}
          findings={findings}
          setFinding={setFinding}
          provisionalDiagnosis={provisionalDiagnosis}
          setProvisionalDiagnosis={setProvisionalDiagnosis}
          referredBy={referredBy} setReferredBy={setReferredBy}
          mrdEdit={mrdEdit} setMrdEdit={setMrdEdit}
          recText={recText} setRecText={setRecText}
          furtherAdvice={furtherAdvice} setFurtherAdvice={setFurtherAdvice}
          license={license} setLicense={setLicense}
          patient={patient}
          rightEarData={rightEarData}
          leftEarData={leftEarData}
          onPrint={openPreflight}
          layoutStatus={layoutStatus}
        />
      )}

      {/* ========== LIVE PREVIEW ========== */}
      <div className="flex-1 overflow-auto bg-gray-300 p-4 print-area">
        <div className="max-w-[210mm] mx-auto mb-2 no-print">
          <LandscapePrompt
            featureKey="report_builder"
            message="Rotate to landscape (or use a tablet) — the A4 preview is 210 mm wide."
            testid="report-builder-landscape"
          />
        </div>
        <div
          id={previewId}
          className={`mx-auto bg-white shadow-lg report-page ${compactLayout ? 'report-compact' : ''}`}
          data-testid={compactLayout ? 'report-compact-on' : 'report-compact-off'}
          // Report typography is driven by the clinic-level `report_font`
          // preference (Settings → Clinic Details → Typography). Falls
          // back to Arial when unset. Applied inline so html2canvas
          // captures the exact family during PDF export.
          style={{
            width: '210mm', minHeight: '297mm', padding: '10mm 12mm',
            fontFamily: clinic?.report_font || 'Arial, sans-serif',
            color: '#1f2937',
          }}
        >
          <ReportHeader clinic={clinic} />
          <PatientStrip
            patient={patient}
            referredBy={referredBy}
            mrd={mrdEdit}
            audiologistName={audiologistName}
          />

          {/* Configurable sections (minus conclusion when tymp uses separate page) */}
          {mainPageSections.map((s) => renderSectionById(s.id, sectionContext))}

          {/* Signature on main page only when Tymp is inline */}
          {!useSeparatePage && (
            <SignatureFooter audiologistName={audiologistName} audiologistUserId={audiologistUserId} license={license} />
          )}

          {/* Tympanometry (separate page) + deferred Results/Recs/Signature */}
          {sections.find((s) => s.id === 'tympanometry' && s.enabled) && useSeparatePage && (
            <div className="report-page-break">
              <header className="flex items-center justify-between border-b-2 border-blue-700 pb-2 mb-3 pt-3">
                <div className="text-[11px] text-gray-700">
                  <span className="font-semibold">{clinic.name}</span>{clinic.tel ? ` · ${clinic.tel}` : ''}
                </div>
                <div className="text-[11px] text-gray-800">
                  <span className="font-bold">{patient.name || '—'}</span> · ID: {patient.patient_id || '—'} · {fmtDate()}
                </div>
              </header>
              <TympanometryFullPage impedance={impedanceData} />

              {sections.find((s) => s.id === 'results' && s.enabled) && (
                <div className="mt-4">
                  <ResultsGridSection entries={buildResultEntries()} />
                </div>
              )}
              {sections.find((s) => s.id === 'recommendations' && s.enabled) && (
                <div className="mt-3">
                  <RecommendationsAdviceSection recommendations={recText} advice={furtherAdvice} />
                </div>
              )}

              <SignatureFooter audiologistName={audiologistName} audiologistUserId={audiologistUserId} license={license} />
            </div>
          )}
        </div>
      </div>

      {/* Preflight "Looks good?" modal — rendered at the panel root so it
          overlays both the sidebar and the preview, and shows before the
          PDF capture runs. */}
      <ReportPreflightModal
        open={preflightOpen}
        onConfirm={confirmPrint}
        onCancel={closePreflight}
        onApplyFix={applyPreflightFix}
        rootElementId={previewId}
      />
    </div>
  );
};

export default ReportsPanel;
